import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { deepClone } from "src/utils/helpers";
import { ConsentFormText } from "./ConsentFormText";
import { ConsentRadioGroup } from "./ConsentRadioGroup";
import { ConsentInputBox } from "./ConsentInputBox";
import { ConsentDate } from "./ConsentDate";
import { setModalContent } from "src/redux/modules/modalEvent";
import { updateSignPadStatus } from "src/redux/actions/questionnaireActions";
import { Logger } from "src/services";
import AppButton from "src/components/base/Button/Button";
import { ConsentPDFViewer, SignatureSpace } from "../SignPad";
import { esignCustomConsent } from "src/redux/services/commonServices";
import "./ConsentForm.scss";
import { ConsentName } from "./ConsentName";

interface Props {
    choice: any;
    onSignature: any;
    handleOnclick: () => void;
}

export const ConsentForm = ({ choice, onSignature, handleOnclick }: Props) => {
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const myRef = useRef(null);
    const [currentPage, setCurrentPage] = useState<any>(0);
    const [consentFormData, setconsentFormData] = useState<any[]>([]);
    const [currentPageData, setCurrentPageData] = useState<any>();
    const [answersObject, setAnswersObject] = useState<any>({});
    const metadata = choice.metadata;

    useEffect(() => {
        getConsentDataFromServer();
    }, []);

    useEffect(() => {
        if (consentFormData) {
            const _answersObject: any = {};

            consentFormData.forEach((consentPage, index) => {
                if (!_answersObject[index]) {
                    _answersObject[index] = {};
                }

                if (consentPage) {
                    consentPage.metadata.forEach((metaDataElement: any) => {
                        if (metaDataElement?.question_type !== "content" && metaDataElement?.question_type !== "consentSignatory") {
                            _answersObject[index][metaDataElement.unique_key] =
                                {
                                    error: "is_required",
                                    answersData: [],
                                };
                        }
                    });
                }
            });
            setAnswersObject(_answersObject);
        }
    }, [consentFormData]);

    function getConsentDataFromServer() {
        const url = metadata?.signed_url;

        return fetch(url)
            .then((response) => response.json())
            .then((responseJson) => {
                if (responseJson && responseJson.length) {
                    setconsentFormData(responseJson);

                    setCurrentPageData(responseJson[0]);
                }

                return responseJson;
            })
            .catch((error) => {
                console.error(error);
            });
    }

    function handleRadioButtonClicked(answersObject: any): void {
        const _answersObject = deepClone(answersObject);

        setAnswersObject(_answersObject);
        toggleCurrentPageValidState(_answersObject);
    }

    function toggleCurrentPageValidState(_answersObject: any): void {
        if (_answersObject) {
            const currentPageAnswerData = _answersObject[currentPage];

            _answersObject[currentPage].valid = true;
            Object.keys(currentPageAnswerData).forEach((key: string) => {
                if (key !== "valid") {
                    if (currentPageAnswerData[key].error) {
                        _answersObject[currentPage].valid = false;
                    }
                }
            });
        }

        setAnswersObject(_answersObject);
    }

    function handlePagination(clickType: "previous" | "next"): void {
        if (clickType === "previous") {
            if (currentPage > 0) {
                const newPageIndex = currentPage - 1;

                setCurrentPage(newPageIndex);
                setCurrentPageData(consentFormData[newPageIndex]);
            }
        }

        if (clickType === "next") {
            if (currentPage < consentFormData.length - 1) {
                const newPageIndex = currentPage + 1;

                setCurrentPage(newPageIndex);
                setCurrentPageData(consentFormData[newPageIndex]);
            }
        }
    }

    function getPrintHTML(base64code: string): string {
        const consentForm: any = document.getElementById("consent-form");
        const formCopy = consentForm?.cloneNode(true);
        const dateInput = formCopy.querySelector(
            "#consent-eSignForm_InputDate"
        );
        const dateInputFormCopy = dateInput?.cloneNode(true);
        const signContainer: any = formCopy.querySelector(
            "#signature-container"
        );
        const signButtonContainer: any = formCopy.querySelector(
            "#signature-button-container"
        );

        signButtonContainer?.remove();

        const signatureBox = document.createElement("div");

        signatureBox.style.cssText = "width: 300px;height:100px";

        const signatureImage = document.createElement("img");

        signatureImage.style.cssText =
            "width: 100%;height:100%;object-fit: cover;";
        signatureImage.src = base64code;

        const signatoryElement: any = formCopy.querySelector(
            "#consent-signatory-name"
        );
        const signatoryBoxCopy = signatoryElement?.cloneNode(true);

        signatureBox?.appendChild(signatureImage);
        signContainer?.appendChild(signatureBox);
        signatureBox?.appendChild(signatoryBoxCopy);
        signatureBox?.appendChild(dateInputFormCopy);
        dateInput?.remove();
        signatoryElement?.remove();

        return formCopy?.innerHTML;
    }

    const signPDF = async (base64code: any) => {
        try {
            const signedConsent = getPrintHTML(base64code);
            const base64EncodedConsent = btoa(signedConsent);
            const res = await esignCustomConsent(
                metadata.doc_id,
                base64EncodedConsent
            );

            onSignature(metadata.doc_id, res);
            dispatch(updateSignPadStatus(false));
        } catch (e: any) {
            Logger.log(e.message);
        }
    };

    const showSignaturePad = () => {
        dispatch(
            setModalContent({
                title: "Draw your signature here",
                modalContent: (
                    <SignatureSpace
                        onSignature={(base64Data: any) => {
                            signPDF(base64Data);
                        }}
                    />
                ),
                className: "signature-pad-modal",
                onToggle: () => {
                    dispatch(updateSignPadStatus(false));
                },
            })
        );
    };

    return (
        <>
            {metadata?.signed === "false" ? (
                <div
                    ref={myRef}
                    className="consent-custom-form"
                    id="consent-form"
                >
                    <div className="consent-custom-content">
                        {currentPageData?.metadata.map(
                            (data: any, index: number) => (
                                <div
                                    key={"metadata_" + index}
                                    style={{ marginBottom: "24px" }}
                                >
                                    {data?.question_type === "content" && (
                                        <ConsentFormText data={data} />
                                    )}
                                    {data?.question_type === "radio" && (
                                        <ConsentRadioGroup
                                            data={data}
                                            answersObject={answersObject}
                                            currentPage={currentPage}
                                            onChoiceSelection={(
                                                answersObject: any
                                            ) => {
                                                handleRadioButtonClicked(
                                                    answersObject
                                                );
                                            }}
                                        />
                                    )}
                                    {data?.question_type === "input" && (
                                        <ConsentInputBox
                                            data={data}
                                            answersObject={answersObject}
                                            currentPage={currentPage}
                                            onDataInput={(
                                                answersObject: any
                                            ) => {
                                                handleRadioButtonClicked(
                                                    answersObject
                                                );
                                            }}
                                        />
                                    )}
                                    {data?.question_type === "date" &&
                                        answersObject[currentPage] && (
                                            <ConsentDate
                                                data={data}
                                                answersObject={answersObject}
                                                currentPage={currentPage}
                                                onDataInput={(
                                                    answersObject: any
                                                ) => {
                                                    handleRadioButtonClicked(
                                                        answersObject
                                                    );
                                                }}
                                            />
                                    )}
                                    {data?.question_type === "consentSignatory" &&
                                        answersObject[currentPage] && (
                                            <ConsentName
                                                data={data}
                                            />
                                    )}
                                </div>
                            )
                        )}
                    </div>
                    {consentFormData?.length > 1 && (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-around",
                                margin: "32px",
                            }}
                        >
                            <AppButton
                                label="Previous"
                                onClick={() => handlePagination("previous")}
                                disabled={currentPage === 0}
                                className="confirm-btn"
                            />
                            <AppButton
                                label="Next"
                                onClick={() => handlePagination("next")}
                                disabled={
                                    !(
                                        answersObject &&
                                        answersObject[currentPage]?.valid
                                    )
                                }
                                className="confirm-btn"
                            />
                        </div>
                    )}
                    <div
                        style={{
                            margin: "2rem",
                        }}
                        id="signature-container"
                    >
                        <div
                            id="signature-button-container"
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <AppButton
                                label="Sign"
                                onClick={() => showSignaturePad()}
                                disabled={
                                    !(
                                        answersObject &&
                                        answersObject[currentPage]?.valid
                                    )
                                }
                                className="confirm-btn"
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <ConsentPDFViewer metadata={metadata} />
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <AppButton
                            isProcessing={loading}
                            label="Continue"
                            onClick={() => {
                                setLoading(true);
                                handleOnclick();
                            }}
                            className="confirm-btn mt-4"
                        />
                    </div>
                </>
            )}
        </>
    );
};
